import * as THREE from 'three';

export interface SelectableMeshOptions {
  excluded?: readonly (THREE.Object3D | null | undefined)[];
  includeNested?: boolean;
  isGroupVisible?: (groupId: number) => boolean;
}

export interface SelectionHitDetails {
  groupId: number | undefined;
  colorKey: string | undefined;
  groupName: string | undefined;
  partName: string | undefined;
  selectionKey: string | undefined;
}

/** Normalize mesh metadata so viewport clicks and other pickers select the same scene-tree item. */
export function selectionDetailsFromHit(hit: THREE.Intersection): SelectionHitDetails {
  const data = hit.object.userData ?? {};
  const groupId = typeof data.groupId === 'number' ? data.groupId : undefined;
  const colorKey = typeof data.colorKey === 'string' ? data.colorKey : undefined;
  const groupName = typeof data.groupName === 'string'
    ? data.groupName
    : groupId !== undefined ? `group_${groupId}` : undefined;
  const partName = typeof data.partName === 'string' ? data.partName : groupName;
  return {
    groupId,
    colorKey,
    groupName,
    partName,
    selectionKey: groupId !== undefined || groupName ? colorKey || groupName || `group_${groupId}` : undefined,
  };
}

export interface ViewportSelectionTree {
  selectTarget(type: 'all' | 'part', id: string, groupId?: number, isMulti?: boolean): void;
  getSelectedGroupIds(): Set<number>;
  getIsIsolated(): boolean;
}

export interface ViewportSelectionEffects {
  tree: ViewportSelectionTree | null;
  setSelectedGroup(group: string): void;
  refreshHighlight(): void;
  updateSelectedGroupPanel(group: string): void;
  setStatus(message: string): void;
}

/** Apply the model viewport's click selection or deselection while respecting isolate mode. */
export function applyViewportSelection(
  hit: THREE.Intersection | undefined,
  isMulti: boolean,
  effects: ViewportSelectionEffects,
): 'selected' | 'cleared' | 'preserved' | 'ignored' {
  if (hit) {
    const details = selectionDetailsFromHit(hit);
    if (!details.selectionKey) return 'ignored';
    effects.tree?.selectTarget('part', details.selectionKey, details.groupId, isMulti);
    const selectedGroup = details.groupName || `group_${details.groupId}`;
    effects.setSelectedGroup(selectedGroup);
    effects.refreshHighlight();
    effects.updateSelectedGroupPanel(selectedGroup);
    const partLabels: Record<string, string> = {
      base: 'Base Frame Housing (Part 0)',
      switch: 'Simulated Switch (Part 1)',
      keycap: 'Keycap Top (Part 2)',
      letter: '3D Embossed Legend (Part 3)',
    };
    const count = effects.tree?.getSelectedGroupIds().size ?? 0;
    effects.setStatus(`Selected (${count} items): ${partLabels[selectedGroup] || details.partName || selectedGroup}`);
    return 'selected';
  }

  if (effects.tree?.getIsIsolated()) return 'preserved';
  effects.setSelectedGroup('all');
  effects.tree?.selectTarget('all', 'all');
  effects.refreshHighlight();
  effects.updateSelectedGroupPanel('all');
  effects.setStatus('Selected: Full Assembly');
  return 'cleared';
}

/** Collect currently visible model meshes used by viewport picking and hover tools. */
export function collectSelectableMeshes(
  root: THREE.Object3D | null | undefined,
  options: SelectableMeshOptions = {},
): THREE.Mesh[] {
  if (!root) return [];
  const excluded = new Set(options.excluded?.filter((item): item is THREE.Object3D => !!item) ?? []);
  const meshes: THREE.Mesh[] = [];
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || !object.visible || object.userData?.isSupport || excluded.has(object)) return;
    if (!options.includeNested && object.parent !== root) return;
    const groupId = object.userData?.groupId;
    if (typeof groupId === 'number' && options.isGroupVisible && !options.isGroupVisible(groupId)) return;
    meshes.push(object);
  });
  return meshes;
}

/** Raycast the visible model and optionally discard hits on the clipped-away side of a section. */
export function pickSelectableIntersections(
  raycaster: THREE.Raycaster,
  root: THREE.Object3D | null | undefined,
  options: SelectableMeshOptions = {},
  clippingPlane?: THREE.Plane | null,
): THREE.Intersection[] {
  const hits = raycaster.intersectObjects(collectSelectableMeshes(root, options));
  return clippingPlane ? hits.filter((hit) => clippingPlane.distanceToPoint(hit.point) >= -0.01) : hits;
}

/** Test a ray against the painted alpha area of a sprite handle, not its full quad. */
export function hitAlphaMaskedSprite(raycaster: THREE.Raycaster, sprite: THREE.Sprite, threshold = 40): boolean {
  const hit = raycaster.intersectObject(sprite, false)[0];
  if (!hit?.uv) return false;
  const mask = sprite.userData.hitMask as Uint8Array | undefined;
  const width = sprite.userData.hitMaskW as number | undefined;
  const height = sprite.userData.hitMaskH as number | undefined;
  if (!mask || !width || !height) return false;
  const x = Math.min(width - 1, Math.max(0, Math.floor(hit.uv.x * width)));
  const y = Math.min(height - 1, Math.max(0, Math.floor((1 - hit.uv.y) * height)));
  return (mask[y * width + x] ?? 0) > threshold;
}

/** Configure the camera-facing plane used to drag a section-cut handle along a world axis. */
export function beginSectionHandleDrag(
  camera: THREE.Camera,
  axis: THREE.Vector3,
  handlePosition: THREE.Vector3,
  dragPlane: THREE.Plane,
): boolean {
  const viewDirection = new THREE.Vector3();
  camera.getWorldDirection(viewDirection);
  const planeNormal = viewDirection.addScaledVector(axis, -viewDirection.dot(axis));
  if (planeNormal.lengthSq() <= 1e-6) return false;
  dragPlane.setFromNormalAndCoplanarPoint(planeNormal.normalize(), handlePosition);
  return true;
}

/** Project a pointer ray onto the section drag plane and return its clamped model percentage. */
export function sectionHandleDragPercent(
  ray: THREE.Ray,
  dragPlane: THREE.Plane,
  root: THREE.Object3D,
  axis: 'x' | 'y' | 'z',
  target: THREE.Vector3,
): number | null {
  if (!ray.intersectPlane(dragPlane, target)) return null;
  const bounds = new THREE.Box3().setFromObject(root);
  if (bounds.isEmpty()) return null;
  const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;
  const position = target.getComponent(axisIndex);
  const min = bounds.min.getComponent(axisIndex);
  const span = bounds.max.getComponent(axisIndex) - min;
  if (span <= 1e-6) return null;
  return Math.min(100, Math.max(0, ((position - min) / span) * 100));
}

/** Owns the geometric state and projection math for dragging a target along one gizmo axis. */
export class AxisDragController {
  private readonly plane = new THREE.Plane();
  private readonly startPosition = new THREE.Vector3();
  private axis: THREE.Vector3 | null = null;
  private grabOffset = 0;

  begin(
    camera: THREE.Camera,
    target: THREE.Object3D,
    axis: THREE.Vector3,
    hit: THREE.Vector3,
  ): boolean {
    const viewDirection = new THREE.Vector3();
    camera.getWorldDirection(viewDirection);
    let normal = viewDirection.clone().addScaledVector(axis, -viewDirection.dot(axis));
    if (normal.lengthSq() < 1e-6) {
      normal = camera.up.clone().addScaledVector(axis, -camera.up.dot(axis));
    }
    if (normal.lengthSq() < 1e-6) return false;

    this.axis = axis.clone().normalize();
    this.plane.setFromNormalAndCoplanarPoint(normal.normalize(), target.position);
    this.grabOffset = hit.clone().sub(target.position).dot(this.axis);
    this.startPosition.copy(target.position);
    return true;
  }

  update(target: THREE.Object3D, hit: THREE.Vector3): { axisValue: number } | null {
    if (!this.axis) return null;
    const delta = hit.clone().sub(this.startPosition).dot(this.axis);
    target.position.copy(this.startPosition).addScaledVector(this.axis, delta - this.grabOffset);
    return { axisValue: target.position.dot(this.axis) };
  }

  intersect(ray: THREE.Ray, target: THREE.Vector3): boolean {
    return this.axis !== null && ray.intersectPlane(this.plane, target) !== null;
  }

  cancel(): void {
    this.axis = null;
  }
}
